import { useEffect, useLayoutEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useGoogleWorkspaceConnected } from '../../hooks/useGoogleWorkspaceConnected';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Input,
  SkeletonText,
  SkeletonBox,
  TableChip,
  SideDrawer,
} from '@/components';
import {
  DashboardPage,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  DashboardTableCell,
} from '../../components/PremiumDesign';
import { TrashIcon } from '../../components/Icons';
import {
  useCustomerQuotation,
  useSendCustomerQuotation,
  useRegenerateCustomerQuotationPdf,
  useDeleteCustomerQuotation,
  useQuotationPdfTemplates,
} from '../hooks';
import {
  CUSTOMER_QUOTE_STATUS_TONE,
  CUSTOMER_QUOTE_STATUS_LABEL,
  DEFAULT_PDF_TEMPLATE_KEY,
  formatQuoteMoney,
  formatQuoteDateTime,
  showDeliveryLine,
  normalizeDeliveryCharge,
  sanitizeDeliveryChargeInput,
  getCompanyId,
  resolveQuotationMediaUrl,
  decodeQuotationId,
  encodeQuotationId,
} from '../utils';
import { QuotationPageHeader } from '../components/QuotationPageHeader';
import {
  PdfTemplatePicker,
  pdfTemplateLabel,
} from '../components/PdfTemplatePicker';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { parseApiError } from '@/common/utils';
import { uploadQuotationToDrive, formatDriveUploadError } from '../../api';

// ─── Design tokens ────────────────────────────────────────────────────────────

const SIDEBAR_SECTION_TITLE =
  'text-overline font-semibold uppercase tracking-wider text-text-secondary mb-2.5';
const DIVIDER = 'border-t border-surface-mint my-3';

// ─── Common UI primitives ─────────────────────────────────────────────────────

function SideCard({ children, className = '' }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-border-default p-4 flex flex-col min-w-0 ${className}`}
    >
      {children}
    </div>
  );
}

function InfoRow({ label, value, href, isExternal = false, className = '' }) {
  return (
    <div
      className={`flex items-start justify-between gap-3 min-w-0 w-full ${className}`}
    >
      <span className="text-body-sm font-medium text-text-secondary shrink-0 pt-0.5 leading-tight">
        {label}
      </span>
      {href ? (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="text-body-sm font-semibold text-primary hover:underline text-right break-all max-w-[60%] leading-snug"
        >
          {value || '—'}
        </a>
      ) : (
        <span className="text-body-sm font-semibold text-text-primary max-w-[60%] text-right break-words [overflow-wrap:anywhere] leading-snug">
          {value || <span className="text-text-secondary font-normal">—</span>}
        </span>
      )}
    </div>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function PageSkeleton() {
  const bar = 'bg-surface-mint animate-pulse rounded';
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] xl:grid-cols-[320px_1fr] gap-5 items-start w-full min-w-0 mt-1">
      {/* left col */}
      <div className="flex flex-col gap-3 w-full min-w-0">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className="bg-white rounded-2xl border border-border-default p-4 flex flex-col gap-3"
          >
            <div className={`h-3 w-20 ${bar}`} />
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <div className={`h-3 w-14 ${bar}`} />
                <div className={`h-3 w-24 ${bar}`} />
              </div>
            ))}
          </div>
        ))}
      </div>
      {/* right col */}
      <div className="flex flex-col gap-4 w-full min-w-0">
        <div className="bg-white rounded-2xl border border-border-default p-5 flex flex-col gap-4">
          <div className={`h-4 w-28 ${bar}`} />
          <div className="rounded-xl border border-border-default overflow-hidden">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 border-b border-surface-mint last:border-0"
              >
                <div className={`h-3 w-5 rounded ${bar}`} />
                <SkeletonBox className="w-[44px] h-[44px] rounded-lg shrink-0" />
                <div className="flex-1 flex flex-col gap-1.5">
                  <SkeletonText width="w-36" height="h-3" />
                  <SkeletonText width="w-20" height="h-2.5" />
                </div>
                <SkeletonText width="w-16" height="h-3" />
                <SkeletonText width="w-12" height="h-3" />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-border-default p-5 flex flex-col gap-4">
          <div className={`h-4 w-36 ${bar}`} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="rounded-xl border border-border-default overflow-hidden"
              >
                <SkeletonBox className="aspect-[16/10] w-full rounded-none" />
                <div className="p-2.5 space-y-1.5">
                  <SkeletonText width="w-20" height="h-3" />
                  <SkeletonText width="w-full" height="h-2.5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Send WhatsApp panel ──────────────────────────────────────────────────────

function SendWhatsAppPanel({ cq, companyId, onDone }) {
  const [phone, setPhone] = useState(cq?.customer_phone || '');
  const [message, setMessage] = useState(
    'Dear customer, please find attached your requested quotation.',
  );
  const sendMutation = useSendCustomerQuotation();

  const handleSend = () => {
    const payload = {};
    if (phone.trim() && phone.trim() !== cq?.customer_phone) {
      payload.customer_phone = phone.trim();
    }
    if (message.trim()) payload.message_text = message.trim();

    sendMutation.mutate(
      { companyId, customerQuotationId: cq.id, payload },
      {
        onSuccess: () => {
          toast.success('Quotation sent via WhatsApp.');
          onDone();
        },
        onError: (err) =>
          toast.error(
            parseApiError(err).message || 'Failed to send quotation.',
          ),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
      <div
        className="absolute inset-0 bg-overlay-backdrop backdrop-blur-sm transition-opacity"
        onClick={onDone}
        aria-hidden="true"
      />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl border border-border-default p-5 sm:p-6 flex flex-col min-w-0 max-w-xl w-full animate-fadeIn">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3.5 border-b border-surface-mint mb-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
              </svg>
            </span>
            <div className="min-w-0">
              <h3 className="text-body font-bold text-text-primary tracking-tight">
                Send via WhatsApp
              </h3>
              <p className="text-body-sm text-text-secondary mt-0.5">
                PDF will be delivered directly to the recipient
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onDone}
            className="text-body-sm text-text-secondary hover:text-text-primary px-2 py-1 rounded-lg hover:bg-form-background-light transition-colors"
          >
            ✕ Close
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-body-sm font-semibold text-text-primary flex items-center justify-between">
              <span>Recipient Phone</span>
              <span className="text-overline font-normal text-text-secondary">
                Include country code
              </span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+919876543210"
              className="w-full h-10 rounded-xl border border-border-default bg-form-background-light px-3.5 text-body font-medium text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:bg-white transition-all"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-body-sm font-semibold text-text-primary">
              Accompanying Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Type your message here…"
              className="w-full rounded-xl border border-border-default bg-form-background-light px-3.5 py-2.5 text-body text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:bg-white transition-all resize-none leading-relaxed"
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-surface-mint">
            <div className="text-overline text-text-secondary flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              PDF document attached automatically
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={onDone}
                disabled={sendMutation.isPending}
                className="h-9 px-3.5 text-xs font-medium"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSend}
                disabled={sendMutation.isPending || !phone.trim()}
                className="h-9 px-4 text-xs font-medium"
              >
                {sendMutation.isPending ? 'Sending…' : 'Send WhatsApp'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Delete confirm overlay ───────────────────────────────────────────────────

function DeleteConfirmOverlay({ onCancel, onConfirm, isPending }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-overlay-backdrop backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full space-y-4 border border-border-default">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
            <TrashIcon className="w-4 h-4" />
          </span>
          <p className="text-h6 font-bold text-text-primary">
            Delete this quotation?
          </p>
        </div>
        <p className="text-body-sm text-text-secondary leading-relaxed">
          This action cannot be undone. The customer quotation and its generated
          PDF will be permanently removed.
        </p>
        <div className="flex gap-2 justify-end pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isPending}
            className="h-9 px-4 text-xs font-medium"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="h-9 px-4 text-xs font-medium !bg-red-500 hover:!bg-red-600 text-white border-transparent"
          >
            {isPending ? 'Deleting…' : 'Delete Quotation'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function CustomerQuotationDetailPage() {
  const { customerQuotationId: rawId } = useParams();
  const customerQuotationId = decodeQuotationId(rawId);
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = getCompanyId();
  const googleDrive = useGoogleWorkspaceConnected(companyId);
  const { can } = usePermissions();
  const canDelete = can(PERMISSIONS.QUOTATIONS.DELETE);

  // If user visits with a raw numeric ID in URL (e.g. /customer/5), replace with encrypted token
  useEffect(() => {
    if (rawId && /^\d+$/.test(rawId)) {
      const encrypted = encodeQuotationId(rawId);
      navigate(`/quotations/customer/${encrypted}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [rawId, navigate, location.state]);

  const [showSendPanel, setShowSendPanel] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isTemplateDrawerOpen, setIsTemplateDrawerOpen] = useState(false);
  const [regenTemplateKey, setRegenTemplateKey] = useState(
    DEFAULT_PDF_TEMPLATE_KEY,
  );
  const [deliveryTerm, setDeliveryTerm] = useState('');
  const [deliveryCharge, setDeliveryCharge] = useState('');

  const {
    data: cq,
    isLoading,
    isError,
  } = useCustomerQuotation({ companyId, customerQuotationId });

  const { data: pdfTemplatesData } = useQuotationPdfTemplates(companyId);
  const pdfTemplates = Array.isArray(pdfTemplatesData)
    ? pdfTemplatesData
    : pdfTemplatesData?.results || [];

  const regenerateMutation = useRegenerateCustomerQuotationPdf();
  const deleteMutation = useDeleteCustomerQuotation();

  const [isUploadingToDrive, setIsUploadingToDrive] = useState(false);
  const { mutate: uploadToDrive } = useMutation({
    mutationFn: () =>
      uploadQuotationToDrive({
        companyId,
        quotationId: cq?.id || cq?.quotation_id,
      }),
    onMutate: () => setIsUploadingToDrive(true),
    onSuccess: (data) => {
      setIsUploadingToDrive(false);
      if (data?.webViewLink) {
        toast.success(
          <span>
            Uploaded to Drive!{' '}
            <a
              href={data.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
            >
              View in Drive
            </a>
          </span>,
          { duration: 6000 },
        );
      } else {
        toast.success('Quotation uploaded to Google Drive successfully.');
      }
    },
    onError: (err) => {
      setIsUploadingToDrive(false);
      toast.error(
        formatDriveUploadError(err, 'Failed to upload quotation to Drive.'),
      );
    },
  });

  // Sync customer name to sessionStorage and global header/document title
  useEffect(() => {
    const customerName = cq?.customer_name || location.state?.customerName;
    if (customerName) {
      try {
        sessionStorage.setItem('quotation-customer-name', customerName);
        window.dispatchEvent(new Event('quotation-customer-name-updated'));
      } catch {
        // ignore
      }
    }
    return () => {
      try {
        sessionStorage.removeItem('quotation-customer-name');
        window.dispatchEvent(new Event('quotation-customer-name-updated'));
      } catch {
        // ignore
      }
    };
  }, [cq?.customer_name, location.state?.customerName]);

  const currency = cq?.currency || 'INR';
  const isSent = cq?.status === 'sent';

  // Keep regen picker in sync with loaded quotation
  useEffect(() => {
    if (cq?.template_key) setRegenTemplateKey(cq.template_key);
    setDeliveryTerm(cq?.delivery_term || '');
    setDeliveryCharge(
      cq?.delivery_charge != null && Number(cq.delivery_charge) !== 0
        ? String(cq.delivery_charge)
        : '',
    );
  }, [cq?.template_key, cq?.delivery_term, cq?.delivery_charge]);

  const handleRegenerate = () => {
    const charge = normalizeDeliveryCharge(deliveryCharge);
    if (charge == null) {
      toast.error('Enter a valid delivery amount.');
      return;
    }
    const payload = {
      ...(regenTemplateKey ? { template_key: regenTemplateKey } : {}),
      delivery_term: deliveryTerm.trim(),
      delivery_charge: charge,
    };

    regenerateMutation.mutate(
      { companyId, customerQuotationId: cq.id, payload },
      {
        onSuccess: () => {
          toast.success('PDF regenerated successfully.');
          setIsTemplateDrawerOpen(false);
        },
        onError: (err) =>
          toast.error(
            parseApiError(err).message || 'Failed to regenerate PDF.',
          ),
      },
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(
      { companyId, customerQuotationId: cq.id },
      {
        onSuccess: () => {
          toast.success('Customer quotation deleted.');
          navigate('/quotations?tab=orders');
        },
        onError: (err) => {
          setShowDeleteConfirm(false);
          toast.error(
            parseApiError(err).message || 'Failed to delete quotation.',
          );
        },
      },
    );
  };

  const handleOpenPdf = () => {
    const url = cq?.pdf_url;
    if (!url) {
      toast.error('PDF is not available yet. Please generate it first.');
      return;
    }
    const resolved = resolveQuotationMediaUrl(url);
    window.open(resolved, '_blank', 'noopener,noreferrer');
  };

  const notes = cq?.raw_input_data?.notes;
  const conversationId = cq?.conversation;

  // ── Derived values ──────────────────────────────────────────────────────────
  const taxAmount = parseFloat(cq?.tax_amount || 0);
  const taxRate = parseFloat(cq?.tax_rate || 0);
  const pdfReady = Boolean(cq?.pdf_url);
  const isRegenerating =
    regenerateMutation.isPending || deleteMutation.isPending;

  useLayoutEffect(() => {
    const el = document.getElementById('dashboard-scroll-container');
    if (el) el.scrollTop = 0;
  }, [rawId]);

  return (
    <DashboardPage className="q-page">
      {showDeleteConfirm && (
        <DeleteConfirmOverlay
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          isPending={deleteMutation.isPending}
        />
      )}

      <div className="w-full flex flex-col gap-4">
        {/* ── Page header ── */}
        <QuotationPageHeader
          title={
            isLoading
              ? 'Loading…'
              : cq?.customer_name
                ? cq.customer_name
                : cq
                  ? `Quote #${cq.quote_number}`
                  : 'Customer Quotation'
          }
          description={cq ? `Quote #${cq.quote_number}` : undefined}
          backTo="/quotations?tab=orders"
          showBorder
          actions={
            cq && (
              <div className="flex flex-wrap items-center gap-2">
                <TableChip
                  tone={CUSTOMER_QUOTE_STATUS_TONE[cq.status] || 'neutral'}
                >
                  {CUSTOMER_QUOTE_STATUS_LABEL[cq.status] ||
                    cq.status ||
                    'UNKNOWN'}
                </TableChip>
                {googleDrive && (
                  <button
                    id="cq-drive-upload-btn"
                    type="button"
                    disabled={isUploadingToDrive || deleteMutation.isPending}
                    onClick={() => uploadToDrive()}
                    title={
                      isUploadingToDrive
                        ? 'Uploading quotation to Google Drive...'
                        : 'Upload quotation PDF to Google Drive'
                    }
                    className="h-8 px-2.5 rounded-lg border border-yellow-300/70 hover:bg-yellow-50 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isUploadingToDrive ? (
                      <svg
                        className="w-4 h-4 animate-spin text-yellow-600"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path d="M12 3a9 9 0 0 1 9 9" opacity="0.3" />
                        <path d="M12 3a9 9 0 0 1 9 9" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-4 h-4">
                        <path
                          fill="#FBBC05"
                          d="M7.71 3.5L1.15 15l3.43 6 6.57-11.5z"
                        />
                        <path
                          fill="#4285F4"
                          d="M22.85 15L16.29 3.5H9.15L15.71 15z"
                        />
                        <path fill="#34A853" d="M4.58 21h14.84l3.43-6H1.15z" />
                      </svg>
                    )}
                    <span className="text-xs font-medium text-gray-600">
                      Drive
                    </span>
                  </button>
                )}
                <Button
                  id="cq-send-whatsapp-btn"
                  type="button"
                  variant={isSent ? 'secondary' : 'primary'}
                  onClick={() => setShowSendPanel((v) => !v)}
                  disabled={!pdfReady || deleteMutation.isPending}
                  title={
                    !pdfReady
                      ? 'Generate a PDF first before sending'
                      : undefined
                  }
                  className="h-8 px-3 text-xs"
                >
                  {isSent ? 'Resend WhatsApp' : 'Send WhatsApp'}
                </Button>
              </div>
            )
          }
        />

        {isLoading ? (
          <PageSkeleton />
        ) : isError || !cq ? (
          /* ── Error state ── */
          <div className="bg-white rounded-2xl border border-border-default p-8 flex flex-col items-center justify-center text-center py-20">
            <p className="text-h6 font-bold text-text-primary">
              {customerQuotationId
                ? `Quotation #${customerQuotationId} not found or has been removed.`
                : 'Unable to load quotation details.'}
            </p>
            <p className="text-body-sm text-text-secondary max-w-sm mt-1 mb-4">
              The quotation record may have been deleted or your link has
              expired.
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                window.history.length > 1
                  ? navigate(-1)
                  : navigate('/quotations?tab=orders')
              }
              className="h-9 px-4 text-xs font-medium"
            >
              ← Back to Quotations
            </Button>
          </div>
        ) : (
          /* ── Two-column layout ── */
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] xl:grid-cols-[320px_1fr] gap-5 items-start w-full min-w-0">
            {/* ════ LEFT SIDEBAR ════ */}
            <div className="flex flex-col gap-3 lg:sticky lg:top-0 w-full min-w-0">
              {/* ── Section 1: Customer Details ── */}
              <SideCard>
                <p className={SIDEBAR_SECTION_TITLE}>Customer Details</p>
                <div className="flex flex-col gap-2.5">
                  <InfoRow label="Name" value={cq.customer_name || '—'} />
                  <InfoRow
                    label="Phone"
                    value={cq.customer_phone}
                    href={
                      cq.customer_phone
                        ? `https://wa.me/${cq.customer_phone.replace(/\D/g, '')}`
                        : undefined
                    }
                    isExternal
                  />
                  {cq.customer_email && (
                    <InfoRow
                      label="Email"
                      value={cq.customer_email}
                      href={`mailto:${cq.customer_email}`}
                    />
                  )}
                  <InfoRow
                    label="Source"
                    value={
                      conversationId ? `#${conversationId}` : 'Manual / Direct'
                    }
                    href={
                      conversationId
                        ? `/whatsapp/conversations/${conversationId}`
                        : undefined
                    }
                  />
                </div>
              </SideCard>

              {/* ── Section 2: Quotation Summary ── */}
              <SideCard>
                <p className={SIDEBAR_SECTION_TITLE}>Quotation Summary</p>
                <div className="flex flex-col gap-2.5">
                  <InfoRow label="Catalog" value={cq.quotation_title || '—'} />
                  <InfoRow
                    label="PDF Design"
                    value={pdfTemplateLabel(pdfTemplates, cq.template_key)}
                  />
                </div>

                {cq.delivery_term && (
                  <>
                    <hr className={DIVIDER} />
                    <p className="text-overline font-semibold uppercase tracking-wider text-text-secondary mb-2">
                      Terms
                    </p>
                    <p className="text-body-sm text-text-primary">
                      Delivery Terms: {cq.delivery_term}
                    </p>
                  </>
                )}

                {notes && (
                  <>
                    <hr className={DIVIDER} />
                    <p className="text-overline font-semibold uppercase tracking-wider text-text-secondary mb-2">
                      Notes
                    </p>
                    <div className="bg-form-background-light border border-border-default rounded-xl p-3 text-body-sm text-text-primary leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere]">
                      {notes}
                    </div>
                  </>
                )}
              </SideCard>

              {/* ── Section 3: Financial Summary ── */}
              <SideCard>
                <p className={SIDEBAR_SECTION_TITLE}>Financial Summary</p>
                <div className="flex flex-col gap-2.5">
                  <InfoRow
                    label="Subtotal"
                    value={formatQuoteMoney(cq.subtotal, currency)}
                  />
                  {taxAmount > 0 && (
                    <InfoRow
                      label={
                        taxRate > 0
                          ? `Tax (${cq.tax_rate}%)`
                          : 'Tax (line items)'
                      }
                      value={formatQuoteMoney(cq.tax_amount, currency)}
                    />
                  )}
                  {showDeliveryLine(cq.delivery_term, cq.delivery_charge) && (
                    <InfoRow
                      label={cq.delivery_term || 'Delivery'}
                      value={formatQuoteMoney(cq.delivery_charge, currency)}
                    />
                  )}
                </div>
                {/* Grand Total — visually emphasized */}
                <div className="mt-3 bg-surface-mint-soft border border-primary/20 rounded-xl px-3.5 py-3 flex items-center justify-between gap-3">
                  <span className="text-body-sm font-bold text-text-primary">
                    Grand Total
                  </span>
                  <span className="text-h6 font-bold text-primary tabular-nums">
                    {formatQuoteMoney(cq.total_amount, currency)}
                  </span>
                </div>
              </SideCard>

              {/* ── Section 4: PDF Actions ── */}
              <SideCard>
                <p className={SIDEBAR_SECTION_TITLE}>PDF Actions</p>

                {/* PDF status indicator */}
                {!pdfReady ? (
                  <div className="mb-3 text-body-sm text-amber-800 bg-amber-50 border border-amber-200/80 rounded-xl p-3 leading-relaxed flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500 mt-0.5 shrink-0" />
                    <span>
                      PDF not generated yet. Select a template below and click{' '}
                      <strong>Apply &amp; Regenerate PDF</strong>.
                    </span>
                  </div>
                ) : (
                  <div className="mb-3 text-body-sm text-primary bg-surface-mint border border-primary/20 rounded-xl px-3 py-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                    <span className="truncate">
                      PDF ready
                      {cq.pdf_generated_at
                        ? ` · ${formatQuoteDateTime(cq.pdf_generated_at)}`
                        : ''}
                    </span>
                  </div>
                )}

                <div className="flex flex-col gap-2 mb-3">
                  <label
                    className="text-[11px] font-medium text-text-secondary"
                    htmlFor="cq-delivery-term"
                  >
                    Delivery terms
                  </label>
                  <Input
                    id="cq-delivery-term"
                    value={deliveryTerm}
                    onChange={(e) => setDeliveryTerm(e.target.value)}
                    placeholder="Ex-Works, Door Delivery, F.O.R."
                    maxLength={255}
                    className="!h-9 text-xs"
                  />
                  <label
                    className="text-[11px] font-medium text-text-secondary"
                    htmlFor="cq-delivery-charge"
                  >
                    Delivery / freight amount
                  </label>
                  <Input
                    id="cq-delivery-charge"
                    type="text"
                    inputMode="decimal"
                    value={deliveryCharge}
                    onChange={(e) =>
                      setDeliveryCharge(
                        sanitizeDeliveryChargeInput(e.target.value),
                      )
                    }
                    placeholder="0.00"
                    className="!h-9 text-xs"
                  />
                  <p className="text-[11px] text-text-muted">
                    Sent with Regenerate PDF. Blank term and 0 hide the line.
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    id="cq-regenerate-pdf-btn"
                    type="button"
                    variant="secondary"
                    onClick={() => setIsTemplateDrawerOpen(true)}
                    disabled={isRegenerating}
                    className="w-full h-9 px-3.5 text-xs font-medium justify-center"
                  >
                    Select Template
                  </Button>

                  {canDelete && (
                    <>
                      <hr className="border-surface-mint mt-1" />
                      <button
                        id="cq-delete-btn"
                        type="button"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={deleteMutation.isPending}
                        className="w-full h-9 px-3.5 flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50/50 text-red-600 text-body-sm font-semibold hover:bg-red-100/60 hover:border-red-300 transition-colors disabled:opacity-40"
                        aria-label="Delete quotation"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                        Delete Quotation
                      </button>
                    </>
                  )}
                </div>
              </SideCard>
            </div>

            {/* ════ RIGHT CONTENT AREA ════ */}
            <div className="flex flex-col gap-5 min-w-0 w-full">
              {/* WhatsApp Send Panel (conditionally shown) */}
              {showSendPanel && (
                <SendWhatsAppPanel
                  cq={cq}
                  companyId={companyId}
                  onDone={() => setShowSendPanel(false)}
                />
              )}

              {/* ── Quotation Items Table ── */}
              <div className="bg-white rounded-2xl border border-border-default p-4 sm:p-5 flex flex-col min-w-0">
                {/* Card header */}
                <div className="flex flex-wrap items-center justify-between gap-3 pb-3.5 border-b border-surface-mint mb-4">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <h3 className="text-body font-bold text-text-primary tracking-tight">
                      Quotation Items
                    </h3>
                    <span className="text-overline font-semibold px-2 py-0.5 rounded-full bg-surface-mint text-primary">
                      {(cq.items || []).length} item
                      {(cq.items || []).length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <Button
                    id="cq-open-pdf-btn"
                    type="button"
                    variant="secondary"
                    onClick={handleOpenPdf}
                    disabled={!pdfReady}
                    title={
                      pdfReady ? 'Open PDF in a new tab' : 'Generate PDF first'
                    }
                    className="h-9 px-4 text-xs font-medium shrink-0"
                  >
                    View PDF
                  </Button>
                </div>

                {(cq.items || []).length === 0 ? (
                  <div className="py-14 flex flex-col items-center justify-center text-center gap-2">
                    <p className="text-body font-semibold text-text-primary">
                      No items in this quotation
                    </p>
                    <p className="text-body-sm text-text-secondary">
                      This quotation has no associated line items.
                    </p>
                  </div>
                ) : (
                  <div className="-mx-4 sm:-mx-5 overflow-x-auto custom-scrollbar">
                    <div className="inline-block min-w-full align-middle px-4 sm:px-5">
                      <div className="rounded-xl border border-surface-mint overflow-hidden">
                        <DashboardTable className="w-full min-w-[560px]">
                          <DashboardTableHeader>
                            <th className="py-3 px-3 font-semibold text-center w-[44px] text-overline uppercase tracking-wider text-surface-mint !rounded-l-[10px]">
                              #
                            </th>
                            <th className="py-3 px-4 font-semibold text-left text-overline uppercase tracking-wider text-surface-mint">
                              Item Description
                            </th>
                            <th className="py-3 px-3 font-semibold text-center w-[64px] text-overline uppercase tracking-wider text-surface-mint">
                              MOQ
                            </th>
                            <th className="py-3 px-4 font-semibold text-right w-[120px] text-overline uppercase tracking-wider text-surface-mint">
                              Unit Price
                            </th>
                            <th className="py-3 px-3 font-semibold text-center w-[60px] text-overline uppercase tracking-wider text-surface-mint">
                              Qty
                            </th>
                            <th className="py-3 px-3 font-semibold text-center w-[64px] text-overline uppercase tracking-wider text-surface-mint">
                              Tax
                            </th>
                            <th className="py-3 px-4 font-semibold text-right w-[130px] text-overline uppercase tracking-wider text-surface-mint !rounded-r-[10px]">
                              Total
                            </th>
                          </DashboardTableHeader>
                          <DashboardTableBody>
                            {cq.items.map((item, idx) => {
                              const thumb = resolveQuotationMediaUrl(
                                item.product_image_url,
                              );
                              return (
                                <DashboardTableRow
                                  key={item.id}
                                  className="group hover:bg-surface-mint-soft transition-colors"
                                >
                                  {/* # */}
                                  <DashboardTableCell
                                    align="center"
                                    className="w-[44px] !py-3 !px-3"
                                  >
                                    <span className="text-body-sm font-bold text-primary">
                                      {idx + 1}
                                    </span>
                                  </DashboardTableCell>

                                  {/* Item: thumbnail + name */}
                                  <DashboardTableCell className="!py-3 !px-4 whitespace-normal">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className="w-[44px] h-[44px] rounded-lg shrink-0 bg-form-background-light overflow-hidden border border-surface-mint flex items-center justify-center">
                                        {thumb ? (
                                          <img
                                            src={thumb}
                                            alt={item.product_name}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <span className="text-text-muted text-overline font-medium">
                                            —
                                          </span>
                                        )}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="text-body font-semibold text-text-primary group-hover:text-primary transition-colors truncate">
                                          {item.product_name}
                                        </p>
                                        {item.product === null && (
                                          <span className="text-overline font-medium text-text-secondary bg-form-background-light rounded-md px-1.5 py-0.5 mt-0.5 inline-block">
                                            Custom item
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </DashboardTableCell>

                                  {/* MOQ */}
                                  <DashboardTableCell
                                    align="center"
                                    className="w-[64px] !py-3 !px-3"
                                  >
                                    <span className="text-body-sm text-text-secondary">
                                      {item.moq ?? '—'}
                                    </span>
                                  </DashboardTableCell>

                                  {/* Unit Price */}
                                  <DashboardTableCell
                                    align="right"
                                    className="w-[120px] !py-3 !px-4 text-body-sm text-text-secondary tabular-nums whitespace-nowrap"
                                  >
                                    {formatQuoteMoney(
                                      item.unit_price,
                                      currency,
                                    )}
                                  </DashboardTableCell>

                                  {/* Qty */}
                                  <DashboardTableCell
                                    align="center"
                                    className="w-[60px] !py-3 !px-3"
                                  >
                                    <span className="inline-block px-2 py-0.5 rounded-lg bg-form-background-light text-body-sm font-bold text-text-primary">
                                      {item.quantity}
                                    </span>
                                  </DashboardTableCell>

                                  {/* Tax */}
                                  <DashboardTableCell
                                    align="center"
                                    className="w-[64px] !py-3 !px-3 text-body-sm text-text-secondary tabular-nums"
                                  >
                                    {item.tax_rate != null &&
                                    item.tax_rate !== ''
                                      ? `${item.tax_rate}%`
                                      : '0%'}
                                    {item.tax_amount != null &&
                                      parseFloat(item.tax_amount) > 0 && (
                                        <span className="block text-[10px] text-text-muted mt-0.5">
                                          {formatQuoteMoney(
                                            item.tax_amount,
                                            currency,
                                          )}
                                        </span>
                                      )}
                                  </DashboardTableCell>

                                  {/* Total */}
                                  <DashboardTableCell
                                    align="right"
                                    className="w-[130px] !py-3 !px-4 text-body font-bold text-text-primary tabular-nums whitespace-nowrap"
                                  >
                                    {formatQuoteMoney(
                                      item.line_total,
                                      currency,
                                    )}
                                  </DashboardTableCell>
                                </DashboardTableRow>
                              );
                            })}
                          </DashboardTableBody>
                        </DashboardTable>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <SideDrawer
          isOpen={isTemplateDrawerOpen}
          onClose={() => setIsTemplateDrawerOpen(false)}
          className="q-drawer"
          title="Select PDF Template"
          width="xl"
        >
          <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
            <PdfTemplatePicker
              companyId={companyId}
              value={regenTemplateKey}
              onChange={setRegenTemplateKey}
              disabled={isRegenerating}
              compact={false}
              showSearch={true}
              label=""
              helperText="Choose a design to regenerate the quotation PDF."
              gridClassName="grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
            />
          </div>
          <div className="flex items-center justify-end gap-3 p-4 border-t border-border-default bg-form-background-light">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsTemplateDrawerOpen(false)}
              disabled={isRegenerating}
              className="h-9 px-4 text-xs font-medium"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleRegenerate}
              disabled={isRegenerating}
              className="h-9 px-4 text-xs font-medium"
            >
              {regenerateMutation.isPending
                ? 'Generating…'
                : 'Apply & Regenerate PDF'}
            </Button>
          </div>
        </SideDrawer>
      </div>
    </DashboardPage>
  );
}

export default CustomerQuotationDetailPage;
